/**
 * ContractorMaintenanceQuotePortal
 * --------------------------------
 * Public, passwordless page where a contractor receiving an RFQ email
 * can submit a labor/parts/lead-time quote for a specific maintenance
 * request. Mirrors SupplierQuotePortal but adapted for work-order data.
 *
 * Route: /contractor/maintenance-quote/:requestId
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench,
  MapPin,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Building2,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContractorMaintenanceQuotePortal() {
  const { requestId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [req, setReq] = useState(null);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [phone, setPhone] = useState("");
  const [laborTotal, setLaborTotal] = useState("");
  const [partsTotal, setPartsTotal] = useState("");
  const [totalEstimate, setTotalEstimate] = useState("");
  const [leadTime, setLeadTime] = useState("");
  const [earliestStart, setEarliestStart] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/maintenance-sourcing/${requestId}/public`);
        setReq(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load this work order.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [requestId]);

  // Auto-compute total when labor/parts change
  const handleLaborOrParts = (which, value) => {
    if (which === "labor") setLaborTotal(value);
    else setPartsTotal(value);

    const l = which === "labor" ? Number(value || 0) : Number(laborTotal || 0);
    const p = which === "parts" ? Number(value || 0) : Number(partsTotal || 0);
    if (!Number.isNaN(l + p)) setTotalEstimate(String(l + p));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Company + email are required");
      return;
    }
    if (!totalEstimate && !laborTotal && !partsTotal) {
      toast.error("Provide at least a total estimate or labor/parts breakdown");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/maintenance-sourcing/${requestId}/public-quote`, {
        contractor_name: name.trim(),
        contractor_email: email.trim(),
        contractor_contact: contact.trim() || undefined,
        contractor_phone: phone.trim() || undefined,
        labor_total: laborTotal ? Number(laborTotal) : undefined,
        parts_total: partsTotal ? Number(partsTotal) : undefined,
        total_estimate: totalEstimate ? Number(totalEstimate) : undefined,
        lead_time: leadTime.trim() || undefined,
        earliest_start: earliestStart || undefined,
        notes: notes.trim() || undefined,
      });
      setSubmitted(true);
      toast.success("Quote submitted — thank you!");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Work order not found</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center" data-testid="contractor-quote-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Quote received</h1>
          <p className="text-slate-600">
            Sterile Mate has your bid for work order <span className="font-mono">{requestId}</span>.
            We'll be in touch within 1 business day. Thanks for partnering with us.
          </p>
        </div>
      </div>
    );
  }

  const items = req?.items || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-quote-portal">
      {/* Header */}
      <header className="bg-[#0a0f1a] border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Wrench className="w-6 h-6 text-amber-400" />
          <div>
            <p className="text-amber-300 text-xs font-bold uppercase tracking-wider">
              Sterile Mate · Contractor RFQ
            </p>
            <p className="text-white text-lg font-semibold">Submit your quote</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Work order summary */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Work Order</p>
              <p className="font-mono text-base font-semibold text-slate-900">{requestId}</p>
            </div>
            {req?.priority && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                req.priority === "emergency" || req.priority === "urgent"
                  ? "bg-red-100 text-red-700"
                  : req.priority === "high"
                  ? "bg-orange-100 text-orange-700"
                  : "bg-blue-100 text-blue-700"
              }`}>
                {req.priority}
              </span>
            )}
          </div>

          {req?.city_state_zip && (
            <p className="text-sm text-slate-600 flex items-center gap-1.5 mb-3">
              <Building2 className="w-3.5 h-3.5 text-slate-400" /> Healthcare partner · {req.city_state_zip}
            </p>
          )}

          {items.length > 0 ? (
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {(it.request_type || "issue").replace(/_/g, " ")}
                    </span>
                    {it.location_details && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {it.location_details}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-800">{it.description}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm text-slate-800">{req?.description || "(see RFQ email for scope)"}</p>
            </div>
          )}
        </section>

        {/* Form */}
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
          <h2 className="text-lg font-bold text-slate-900">Your quote</h2>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Company name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="cq-company" />
            </div>
            <div>
              <Label>Reply-to email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="cq-email" />
            </div>
            <div>
              <Label>Contact name</Label>
              <Input value={contact} onChange={(e) => setContact(e.target.value)} data-testid="cq-contact" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="cq-phone" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
            <div>
              <Label>Labor total ($)</Label>
              <Input type="number" min="0" step="0.01" value={laborTotal} onChange={(e) => handleLaborOrParts("labor", e.target.value)} data-testid="cq-labor" />
            </div>
            <div>
              <Label>Parts total ($)</Label>
              <Input type="number" min="0" step="0.01" value={partsTotal} onChange={(e) => handleLaborOrParts("parts", e.target.value)} data-testid="cq-parts" />
            </div>
            <div>
              <Label>Grand total ($) *</Label>
              <Input type="number" min="0" step="0.01" value={totalEstimate} onChange={(e) => setTotalEstimate(e.target.value)} required data-testid="cq-total" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Lead time</Label>
              <Input value={leadTime} onChange={(e) => setLeadTime(e.target.value)} placeholder="e.g. 3-5 business days" data-testid="cq-leadtime" />
            </div>
            <div>
              <Label>Earliest start</Label>
              <Input type="date" value={earliestStart} onChange={(e) => setEarliestStart(e.target.value)} data-testid="cq-earliest" />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Scope assumptions, exclusions, COI status, etc." data-testid="cq-notes" />
          </div>

          <Button type="submit" disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700 gap-2" data-testid="cq-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Submit quote
          </Button>
          <p className="text-xs text-slate-500 text-center">
            No login required. Your submission is keyed to work order {requestId}.
          </p>
        </form>
      </div>
    </div>
  );
}
